// Primitives shared by the chart-card image export and the full-page report
// export. Kept in one place because the data:-URI detail below is subtle and
// silently drops charts from an export when it is got wrong.

export const EXPORT_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// Copy the computed presentation styles onto the clone. A detached SVG has no
// stylesheet, so anything driven by CSS rather than attributes would otherwise
// rasterise as unstyled black shapes.
export function inlineStyles(origSvg, cloneSvg) {
  const origEls = origSvg.querySelectorAll('*');
  const cloneEls = cloneSvg.querySelectorAll('*');
  origEls.forEach((orig, i) => {
    if (!cloneEls[i]) return;
    const cs = window.getComputedStyle(orig);
    const props = ['font-family','font-size','font-weight','fill','stroke','stroke-width','stroke-dasharray',
      'stroke-linecap','stroke-linejoin','opacity','text-anchor','dominant-baseline','visibility','display','color'];
    const style = props.map(p => `${p}:${cs.getPropertyValue(p)}`).join(';');
    cloneEls[i].setAttribute('style', style);
  });
}

export async function svgToImage(svgEl, w, h) {
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  // Use passed dimensions (transform-safe) or fall back to getBoundingClientRect
  const width = w || svgEl.getBoundingClientRect().width;
  const height = h || svgEl.getBoundingClientRect().height;
  clone.setAttribute('width', width);
  clone.setAttribute('height', height);
  clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  inlineStyles(svgEl, clone);
  const data = new XMLSerializer().serializeToString(clone);
  // A data: URI, not a blob: URL. The app's CSP allows `data:` under img-src
  // but not `blob:` (server/index.js), so a blob URL is refused, onerror
  // fires, and the chart is silently dropped from the export — which is why
  // shared images arrived containing only the title.
  const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(data);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Wait for recharts to lay a chart out inside `root` and settle. Animation is
// normally disabled for exports, but rather than trust that for every chart
// type this waits until the rendered geometry stops changing.
export async function waitForCharts(root, expected, timeoutMs = 4000) {
  const deadline = Date.now() + timeoutMs;
  const found = () => root ? root.querySelectorAll('svg.recharts-surface') : [];
  while (Date.now() < deadline) {
    const svgs = found();
    if (svgs.length >= expected && [...svgs].every(s => s.querySelector('.recharts-layer'))) break;
    await new Promise(r => setTimeout(r, 50));
  }
  const geometry = () => [...found()]
    .map(s => [...s.querySelectorAll('path')].map(el => el.getAttribute('d') || '').join('|')).join('#').length;
  let last = -1, stable = 0;
  while (Date.now() < deadline && stable < 2) {
    await new Promise(r => setTimeout(r, 80));
    const now = geometry();
    stable = now === last ? stable + 1 : 0;
    last = now;
  }
  return found();
}

// Rounded rectangle path — canvas has no primitive for one in older Safari.
export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Greedy word wrap against the canvas' current font.
export function wrapText(ctx, text, maxWidth) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width > maxWidth && line) { lines.push(line); line = w; }
    else line = next;
  }
  if (line) lines.push(line);
  return lines;
}
